import React, { useEffect, useState } from 'react';
import { KeyValueItem } from '@gerdau/hefesto-mais-web/KeyValueItem';
import {
  IOpenOrderStatusState,
  IOpenOrderStatusDataBySku
} from '../../../../store/ducks/dashboard/types';
import { formatNumberTon } from '../../../../utils/formatNumbers';
import { ContainerInfo, SummaryContainerCell, TableContainer } from './styles';
import Table from '../../../../components/Table';

interface IOpenOrderStatusTableDataSource {
  key: number;
  sku: string;
  productDescription: string;
  totalDelivery: string;
  totalPending: string;
  totalRequested: string;
  totalShipment: string;
}

interface IOpenOrderStatusBySkuTable {
  openOrderStatusState: IOpenOrderStatusState;
}

const OpenOrderStatusBySkuTable: React.FC<IOpenOrderStatusBySkuTable> = ({
  openOrderStatusState
}) => {
  const [openOrderStatusData, setOpenOrderStatusData] = useState<
    IOpenOrderStatusTableDataSource[]
  >([]);

  const [totalRow, setTotalRow] = useState<IOpenOrderStatusTableDataSource>({
    key: 0,
    sku: 'Total',
    productDescription: ' - ',
    totalDelivery: '0',
    totalPending: '0',
    totalRequested: '0',
    totalShipment: '0'
  });

  useEffect(() => {
    updateOpenOrderData();
  }, [openOrderStatusState.dashboardBySku]);

  const updateOpenOrderData = () => {
    if (openOrderStatusState.dashboardBySku) {
      const totalRow: IOpenOrderStatusTableDataSource = {
        key: 0,
        sku: 'Total',
        productDescription: ' - ',
        totalDelivery: '0',
        totalPending: '0',
        totalRequested: '0',
        totalShipment: '0'
      };
      const newOpenOrderStatusData = openOrderStatusState.dashboardBySku.map(
        (item: IOpenOrderStatusDataBySku, idx: number) => {
          const {
            product,
            productDescription,
            totalDelivery,
            totalPending,
            totalRequested,
            totalShipment
          } = item;

          totalRow.totalDelivery = (
            Number(totalRow.totalDelivery) +
            Number(totalDelivery.replace(/,/g, ''))
          ).toString();
          totalRow.totalPending = (
            Number(totalRow.totalPending) +
            Number(totalPending.replace(/,/g, ''))
          ).toString();
          totalRow.totalRequested = (
            Number(totalRow.totalRequested) +
            Number(totalRequested.replace(/,/g, ''))
          ).toString();
          totalRow.totalShipment = (
            Number(totalRow.totalShipment) +
            Number(totalShipment.replace(/,/g, ''))
          ).toString();

          return {
            key: idx + 1,
            sku: product.toString(),
            productDescription,
            totalDelivery,
            totalPending,
            totalRequested,
            totalShipment
          };
        }
      );

      setTotalRow(totalRow);
      setOpenOrderStatusData(newOpenOrderStatusData);
    }
  };

  const getTonNumberFormatted = (text: string): string => {
    text = text.replace(/,/g, '');

    if (!text || !Number(text)) {
      return ' - ';
    } else {
      return formatNumberTon(text);
    }
  };

  const getColumns = () => {
    const columns = [
      {
        title: 'SKU',
        dataIndex: 'sku',
        sorter: (
          a: IOpenOrderStatusTableDataSource,
          b: IOpenOrderStatusTableDataSource
        ) => Number(a.sku.replace('-', '')) - Number(b.sku.replace('-', '')),
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Descrição do produto',
        dataIndex: 'productDescription',
        sorter: (
          a: IOpenOrderStatusTableDataSource,
          b: IOpenOrderStatusTableDataSource
        ) =>
          Number(a.productDescription.replace('-', '')) -
          Number(b.productDescription.replace('-', '')),
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Solicitado',
        dataIndex: 'totalRequested',
        sorter: (
          a: IOpenOrderStatusTableDataSource,
          b: IOpenOrderStatusTableDataSource
        ) =>
          +a.totalRequested.toString().replace(',', '') -
          +b.totalRequested.toString().replace(',', ''),
        render: (text: string) => <>{getTonNumberFormatted(text)}</>
      },
      {
        title: 'Em remessa',
        dataIndex: 'totalDelivery',
        sorter: (
          a: IOpenOrderStatusTableDataSource,
          b: IOpenOrderStatusTableDataSource
        ) =>
          +a.totalDelivery.toString().replace(',', '') -
          +b.totalDelivery.toString().replace(',', ''),
        render: (text: string) => <>{getTonNumberFormatted(text)}</>
      },
      {
        title: 'Em carregamento',
        dataIndex: 'totalShipment',
        sorter: (
          a: IOpenOrderStatusTableDataSource,
          b: IOpenOrderStatusTableDataSource
        ) =>
          +a.totalShipment.toString().replace(',', '') -
          +b.totalShipment.toString().replace(',', ''),
        render: (text: string) => <>{getTonNumberFormatted(text)}</>
      },
      {
        title: 'Saldo',
        dataIndex: 'totalPending',
        sorter: (
          a: IOpenOrderStatusTableDataSource,
          b: IOpenOrderStatusTableDataSource
        ) =>
          +a.totalPending.toString().replace(',', '') -
          +b.totalPending.toString().replace(',', ''),
        render: (text: string) => <>{getTonNumberFormatted(text)}</>
      }
    ];

    return columns;
  };

  return (
    <>
      <ContainerInfo>
        <SummaryContainerCell>
          <KeyValueItem title="">Total</KeyValueItem>
        </SummaryContainerCell>
        <SummaryContainerCell>
          <KeyValueItem title="Solicitado">{`${formatNumberTon(
            totalRow.totalRequested.toString()
          )} t`}</KeyValueItem>
        </SummaryContainerCell>
        <SummaryContainerCell>
          <KeyValueItem title="Em remessa">{`${formatNumberTon(
            totalRow.totalDelivery.toString()
          )} t`}</KeyValueItem>
        </SummaryContainerCell>
        <SummaryContainerCell>
          <KeyValueItem title="Em carregamento">{`${formatNumberTon(
            totalRow.totalShipment.toString()
          )} t`}</KeyValueItem>
        </SummaryContainerCell>
        <SummaryContainerCell>
          <KeyValueItem title="Saldo">{`${formatNumberTon(
            totalRow.totalPending.toString()
          )} t`}</KeyValueItem>
        </SummaryContainerCell>
      </ContainerInfo>
      <TableContainer>
        <Table
          columns={getColumns()}
          data={openOrderStatusData}
          hasPagination
        />
      </TableContainer>
    </>
  );
};

export default OpenOrderStatusBySkuTable;
